<template>
  <div class="recent-hosts-container">
    <button
      class="recent-hosts-btn"
      :title="`Recent hosts (${hosts.length})`"
      aria-label="Show recent hosts"
      @click="isOpen = !isOpen"
      :disabled="hosts.length === 0"
    >
      <Clock :size="16" />
    </button>

    <div v-if="isOpen" class="dropdown-overlay" @click.self="isOpen = false">
      <div class="dropdown-menu">
        <div class="dropdown-header">
          <span class="header-title">Recent Hosts</span>
          <button
            class="close-btn"
            aria-label="Close dropdown"
            @click="isOpen = false"
          >
            <X :size="14" />
          </button>
        </div>

        <div v-if="hosts.length === 0" class="empty-state">
          <p>No recent hosts saved</p>
        </div>

        <ul v-else class="hosts-list">
          <li v-for="host in hosts" :key="host" class="host-item">
            <button
              class="host-btn"
              @click="selectHost(host)"
              :title="host"
            >
              <span class="host-url">{{ truncateUrl(host) }}</span>
              <button
                class="remove-btn"
                @click.stop="removeHost(host)"
                aria-label="Remove host"
                title="Remove from history"
              >
                <X :size="12" />
              </button>
            </button>
          </li>
        </ul>

        <div v-if="hosts.length > 0" class="dropdown-footer">
          <button
            class="clear-btn"
            @click="clearAllHosts"
            aria-label="Clear all hosts"
          >
            Clear history
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from "vue";
import { Clock, X } from "lucide-vue-next";
import { useRecentHostsStore } from "@/stores/recentHosts";

interface Emits {
  (e: "select", host: string): void;
}

const emit = defineEmits<Emits>();
const recentHostsStore = useRecentHostsStore();
const isOpen = ref(false);

const hosts = computed(() => recentHostsStore.getHosts());

function selectHost(host: string): void {
  emit("select", host);
  isOpen.value = false;
}

function removeHost(host: string): void {
  recentHostsStore.removeHost(host);
}

function clearAllHosts(): void {
  if (confirm("Clear all recent hosts?")) {
    recentHostsStore.clearHosts();
    isOpen.value = false;
  }
}

function truncateUrl(url: string, maxLength: number = 40): string {
  if (url.length <= maxLength) {
    return url;
  }
  return url.substring(0, maxLength - 3) + "...";
}
</script>

<style scoped lang="scss">
.recent-hosts-container {
  position: relative;
  display: inline-block;
}

.recent-hosts-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  background: var(--bg-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: var(--accent);
    color: var(--accent);
    background: var(--bg-surface);
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }
}

.dropdown-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  min-width: 280px;
  max-width: 400px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  z-index: 1001;
  animation: slideDown 0.2s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;

  .header-title {
    font-weight: 600;
    font-size: 0.9rem;
    color: var(--text-primary);
  }

  .close-btn {
    background: none;
    border: none;
    color: var(--text-secondary, var(--text-primary));
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    opacity: 0.6;
    transition: opacity 0.2s;

    &:hover {
      opacity: 1;
      color: var(--accent);
    }
  }
}

.empty-state {
  padding: 24px 16px;
  text-align: center;
  color: var(--text-secondary, var(--text-primary));
  font-size: 0.85rem;

  p {
    margin: 0;
  }
}

.hosts-list {
  list-style: none;
  margin: 0;
  padding: 4px;
  flex: 1;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 3px;

    &:hover {
      background: var(--text-secondary, var(--text-primary));
    }
  }
}

.host-item {
  margin: 0;
}

.host-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: all 0.2s;
  font-size: 0.85rem;

  &:hover {
    background: var(--bg-elevated);
    color: var(--accent);
  }

  &:active {
    transform: scale(0.98);
  }

  .host-url {
    flex: 1;
    text-align: left;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: monospace;
    font-size: 0.8rem;
  }

  .remove-btn {
    flex-shrink: 0;
    background: none;
    border: none;
    color: var(--text-secondary, var(--text-primary));
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    opacity: 0.5;
    transition: all 0.2s;

    &:hover {
      opacity: 1;
      color: var(--danger, #e55);
    }
  }
}

.dropdown-footer {
  padding: 8px 8px;
  border-top: 1px solid var(--border);
  flex-shrink: 0;

  .clear-btn {
    width: 100%;
    padding: 6px 10px;
    background: var(--bg-elevated);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-secondary, var(--text-primary));
    cursor: pointer;
    font-size: 0.8rem;
    transition: all 0.2s;

    &:hover {
      background: var(--bg-surface);
      border-color: var(--danger, #e55);
      color: var(--danger, #e55);
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .dropdown-menu {
    animation: none;
  }

  .host-btn:active {
    transform: none;
  }
}
</style>
